// Imports
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncorrectChamp } from '../incorrect-champ/IncorrectChamp';
import { InputSuggestion } from '../input-suggestions/InputSuggestion';
import { Lifes } from '../lifes/Lifes';
import { LoseModal } from '../modals/lose/LoseModal';
import { WinModal } from '../modals/win/WinModal';
import { Suggested } from '../suggested/Suggested';
import { Square } from './square/Square';
import './Board.css';

interface LeagueData {
  positions: string[];
  region: string[];
  releaseYears: number[];
  species: string[];
  range: string[];
  resource: string[];
  gender: string[];
}

interface ChampionData {
  champions: Champion[];
}

// Define a TypeScript interface for a Champion
interface Champion {
    id: number;
    name: string;
    positions: string[];
    region: string[];
    species: string[];
    releaseYear: string[];
    resource: string[];
    gender: string[];
    range: string[];
    src: string;
}

interface BoardData {
  boards: {
      [key: string]: string[];
  };
}

// Main component
export const Board = () => {

  // State variables
  const [squareId, setSquareId] = useState<string>("");
  const [squareSelected, setSquareSelected] = useState<string>("");
  const [championDictionary, setChampionDictionary] = useState<Record<string, Champion[]>>({});
  const [championsCorrectDictionary, setChampionsCorrectDictionary] = useState<Record<string, Champion>>({});
  const [champs, setChamps] = useState<Champion[]>([]);
  const [champsSuggested, setChampsSuggested] = useState<Champion[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [board, setBoard] = useState([
    ["", "", "", "", ""],
    ["", "", "", "", ""],
    ["", "", "", "", ""],
    ["", "", "", "", ""]
  ]);
  const [lifes, setLifes] = useState<number>();
  const [maxLifes, setMaxLifes] = useState(10)
  const [winGame, setWinGame] = useState<number>();
  const [gameOver, setGameOver] = useState<boolean>();
  const [winStreak, setWinStreak] = useState<number>(0);
  const [squaresCorrects, setSquaresCorrect] = useState<string[]>([])
  const {i18n, t} = useTranslation();
  const [dataJSON, setDataJSON] = useState<LeagueData>();
  const [championsJSON, setChampionsJSON] = useState<ChampionData>();
  const [boards, setBoards] = useState<BoardData>();
  const [clues, setClues] = useState<Array<string>>();

  useEffect(() => {
    //DATA REGIONS POSITIONS ETC
    import(`./data-${i18n.language}.json`).then(data => {
      setDataJSON(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON data", error);
    });

    //DATA CHAMPIONS
    import(`./champs-${i18n.language}.json`).then(data => {
      setChampionsJSON(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON CHAMPIONS", error);
    });
    
    //DATA BOARDS
    import(`./boards-${i18n.language}.json`).then(data => {
      setBoards(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON BOARDS", error);
    });

  }, [i18n.language]);

  useEffect(() => {
    setupBoard();
  }, [boards]);

  const playSound = () => { 
    const audioInstance = new Audio("mondongo-sound.mp3");

    if (audioInstance) {
      audioInstance.play();
    }
  }

  useEffect(() => {
    if(championsJSON) {
      setChamps(championsJSON.champions)
    }
  }, [championsJSON]);

  // Helper function to check if a champion is valid based on given tracks
  const isChampionValid = (champion: Champion, tracks: string[]) => {
    // Iterate through the provided criteria
    for (const track of tracks) {
      let option: keyof Champion;
      option = "positions"
      // Determine the key to check based on the criterion
      if(dataJSON) {
        if (dataJSON.positions.includes(track)) {
          option = "positions";
        } else if (!isNaN(parseInt(track))) {
          option = "releaseYear";
        } else {
          if (dataJSON.region.includes(track)) {
            option = "region";
          } else if (dataJSON.range.includes(track)) {
            option = "range";
          } else if (dataJSON.gender.includes(track)) {
            option = "gender";
          } else if (dataJSON.resource.includes(track)) {
            option = "resource";
          } else {
            option = "species"
          }
        }
      }

      if (!champion[option].includes(track)) {
        incorrectChampion(champion);
        return false;
      }
    }

    setSquaresCorrect((prevSquares) => {
      return [...prevSquares, squareId]
    })

    return true;
  };


  // Handle the selection of an incorrect champion
  const incorrectChampion = async (champion: Champion) => {
    if (champion) {
      setChampionDictionary((prevDictionary) => {
        // Clone the previous dictionary to make changes
        const updatedDictionary = { ...prevDictionary };

        if (!updatedDictionary[squareId]) {
          // If there's no entry for the square, create one with the incorrect champion
          updatedDictionary[squareId] = [champion];
        } else {
          // If there's an entry for the square, add the incorrect champion to the list if it's not already there
          if (!updatedDictionary[squareId].some(existingChamp => existingChamp.id === champion.id)) {
            updatedDictionary[squareId] = [champion, ...updatedDictionary[squareId]];
          }
        }

        return updatedDictionary;
      });

      if (lifes) {
        // Reduce the number of remaining lives
        setLifes(lifes - 1);
        // Update the remaining lives in local storage
        const lifesStorage = localStorage.getItem('lifesStorage');
        if (lifesStorage !== null) {
          const currentLifes = parseInt(lifesStorage);
          const newLifes = currentLifes - 1;
          localStorage.setItem('lifesStorage', newLifes.toString());
        }
      }

      if (lifes === 1) {
        // If there is only 1 life remaining, trigger the game over and defeat logic
        setGameOver(true);
        playSound();
        setSquareSelected("");
        handleDefeat();
      }
    }
  };

  // Upload the selected champion to the board
  const uploadBoard = (champion: Champion) => {
    // Check if there's a stored value for the number of wins
    const winGameStorage = localStorage.getItem('winGameStorage');
    const correctChamps = localStorage.getItem('championCorrectDictionaryStorage');

    if (correctChamps && correctChamps.length > 0) {
      // Parse and update the dictionary of correct champions
      const parsedCorrectChamps = JSON.parse(correctChamps);
      setChampionsCorrectDictionary((prevDictionary) => {
        const newDictionary = { ...prevDictionary };
        for (const key in parsedCorrectChamps) {
          if (parsedCorrectChamps.hasOwnProperty(key)) {
            newDictionary[key] = parsedCorrectChamps[key];
          }
        }
        newDictionary[squareId] = champion;
        return newDictionary;
      });
    }

    // Increment the number of wins and update the local storage
    if (winGameStorage !== null) {
      const currentWin = parseInt(winGameStorage);
      const newWin = currentWin + 1;
      localStorage.setItem('winGameStorage', newWin.toString());
    }

    // Update the winGame state
    if (winGame === 0) {
      setWinGame(1);
    } else {
      if (winGame) setWinGame(winGame + 1);
    }

    // Add the champion image to the specified square
    const squareElement = document.getElementById(squareId);
    if (squareElement) {
      addImg(squareId, champion, 1);
    }

    // Reset state variables
    setClues([])
    setSquareId("");
    setInputValue("");
    setSquareSelected("");
  };


  // Add an image to a square
  const addImg = (id: string, champion: Champion, newSquare: number) => {
    // Find the square element by its ID
    let square: HTMLElement | null = document.getElementById(id);
    
    if (square) {
      // Create an image element and set its properties
      const imgElement = document.createElement("img");
      imgElement.setAttribute("data-key", square.id + "img");
      imgElement.classList.add("imgSquare", "scaleSquare");
      imgElement.src = champion.src;
      square.style.border = "3px solid #009500";
      square.innerHTML = ' ';
      square.classList.add("vibrate");

      square.appendChild(imgElement);
      square.addEventListener("click", showErrorsOnCorrectChamp);
    }
  }

  const [isCorrectSquare, setIsCorrectSquare] = useState<boolean>(false)
  // Show errors on the correct champion selection
  const showErrorsOnCorrectChamp = (event: Event) => {
    // Get the target element and its parent
    const targetElement = event.target as HTMLElement;

    let id: string = "";

    if (targetElement) {
      // Extract the ID of the square from its parent
      id = targetElement.id;
    }
    
    if (squareId) {
      // Remove the "selected" class from the previously selected square
      const previousElement = document.getElementById(squareId);
      if (previousElement) {
        previousElement.classList.remove("selected");
      }
    }

    // Reset the selected square
    setIsCorrectSquare(true)
    setSquareSelected(id);
    setSquareId(id);

    targetElement.classList.add("selected");
  };

  // Handle square selection
  const handleSquareSelection = (event: Event) => {
    event.preventDefault();
    const targetElement = event.target as HTMLElement;

    if (targetElement.getAttribute("data-name") === "game") {
      const id: string = targetElement.id;
      const row: string = "square"+id.charAt(6)+"0";
      const column: string = "square0"+id.charAt(7);
      const lastColumn: string = "square"+id.charAt(6)+"4";

      setClues([row,column,lastColumn])

      // Remove the "selected" class from the previously selected square
      if (squareId) {
        const previousElement = document.getElementById(squareId);
        if (previousElement) {
          previousElement.classList.remove("selected");
        }
      }

      // Check if the square has an image child
      const hasImageChild = Array.from(targetElement.children).some(child => child instanceof HTMLImageElement);
      setSquareSelected(id);
      setSquareId(id);
  
      if (!hasImageChild) {
        // Update the selected square ID and set it as squareId
        setIsCorrectSquare(false);
      } else {
        setIsCorrectSquare(true);
      }

      // Set focus on the input element with the ID "inputSuggestions"
      const inputElement = document.getElementById("inputSuggestions") as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    }
  }

  // Set up the game board
  const setupBoard = () => {
    let randomBoard;

    if(boards) {
      // Check if a random board configuration is stored in local storage
      const dayBoard = localStorage.getItem('countDayBoard')
      let numberDay: number;
      
      if(dayBoard && parseInt(dayBoard) > 17) {
        localStorage.setItem('countDayBoard', "1")
        numberDay = 1;
      } else {
        numberDay = dayBoard ? parseInt(dayBoard) : 1;
      }

      randomBoard = boards.boards[numberDay];

      localStorage.setItem('randomBoardOverStorage', JSON.stringify(randomBoard));

      const newBoard = [
        ["", randomBoard[0], randomBoard[1], randomBoard[2], ""],
        [randomBoard[3], "", "", "", randomBoard[6]],
        [randomBoard[4], "", "", "", randomBoard[7]],
        [randomBoard[5], "", "", "", randomBoard[8]]
      ];

      setBoard(newBoard);
    }
  }

  // Set up event listeners for empty squares
  const setupEventListeners = () => {
      const squaresArray = document.querySelectorAll('[data-name="game"]');
      squaresArray.forEach(function (element) {
        if (element.getAttribute("data-value") === "squarePlay") {
          element.addEventListener("click", handleSquareSelection);
        }
      });
  };

  // Handle defeat and modal opening
  const [isDefeatModalOpen, setIsDefeatModalOpen] = useState(false);
  const handleDefeat = () => {
    setIsDefeatModalOpen(true);
    setClues([])
    document.body.style.overflow = 'hidden';
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleCloseDefeatModal = () => {
    setIsDefeatModalOpen(false);
    document.body.style.overflow = 'auto';
    document.body.style.overflowY = 'scroll';
  };
  
  // Handle win and modal opening
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);
  const handleWin = () => {
    setIsWinModalOpen(true);
    document.body.style.overflow = 'hidden';
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleCloseWinModal = () => {
    setIsWinModalOpen(false);
    document.body.style.overflow = 'auto';
    document.body.style.overflowY = 'scroll';
  };

  const checkNewDay = () => {
    const dateStorage = localStorage.getItem('dateStorage');
    if(!dateStorage) {
      const date = new Date();
      localStorage.setItem('dateStorage', date.toDateString());
    } else {
      const date = new Date();
      date.setHours(0, 0, 0, 0);

      const dateStore = new Date(dateStorage)
      dateStore.setHours(0, 0, 0, 0);

      if(dateStore < date) {
        localStorage.removeItem('gameStartedStorage');
        let dayBoard = localStorage.getItem('countDayBoard')
        if(dayBoard) {
          let numberDay = parseInt(dayBoard) + 1 
          localStorage.setItem('countDayBoard', numberDay.toString())
        }
        localStorage.setItem('dateStorage', date.toDateString());
      }
    }
  }

  // Initial setup and load data from local storage
  useEffect(() => {

    const countDayBoard = localStorage.getItem('countDayBoard');

    if(!countDayBoard) {
      localStorage.setItem('countDayBoard', "1")
    }

    import(`./data-${i18n.language}.json`).then(data => {
      setDataJSON(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON data", error);
    });

    import(`./champs-${i18n.language}.json`).then(data => {
      setChampionsJSON(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON CHAMPIONS", error);
    });
    
    import(`./boards-${i18n.language}.json`).then(data => {
      setBoards(data);
    }).catch(error => {
      console.error("Error cargando el archivo JSON BOARDS", error);
    });

    checkNewDay();
    const gameStartedStorage = localStorage.getItem('gameStartedStorage');
    const winStreakStorage = localStorage.getItem('winStreakStorage');

    if(!winStreakStorage){
      localStorage.setItem('winStreakStorage', "0");
    } else {
      setWinStreak(parseInt(winStreakStorage))
    }
    setupEventListeners();
    const initializeGame = () => {
      localStorage.setItem('randomBoardOverStorage', "");
      setupBoard();
      setLifes(maxLifes);
      setWinGame(0);
      setGameOver(false);
      localStorage.setItem("winFinished", "false")
      localStorage.setItem('gameStartedStorage', "true");
      localStorage.setItem('lifesStorage', maxLifes.toString());
      localStorage.setItem('winGameStorage', "0");
      localStorage.setItem('gameOverStorage', "false");
      localStorage.setItem('championDictionaryStorage', JSON.stringify(championDictionary));
      localStorage.setItem('championCorrectDictionaryStorage', JSON.stringify(championsCorrectDictionary));
    };

    if (champs.length === 0) {
      if(championsJSON) 
        setChamps(championsJSON.champions);
    }

    if (!gameStartedStorage) {
      initializeGame();
    } else {
      const lifesStorage = localStorage.getItem('lifesStorage');
      if (lifesStorage) {
        const parsedLifes = parseInt(lifesStorage);
        setLifes(parsedLifes);
        if (parsedLifes <= 0) {
          setGameOver(true);
        }
      }

      const winGameStorage = localStorage.getItem('winGameStorage');
      if (winGameStorage) {
        const parsedWinGame = parseInt(winGameStorage);
        setWinGame(parsedWinGame);
      }

      setupBoard();

      const championDictionaryStorage = localStorage.getItem('championDictionaryStorage');
      if (championDictionaryStorage) {
        const champsError = JSON.parse(championDictionaryStorage);
        for (const key in champsError) {
          if (champsError.hasOwnProperty(key)) {
            const champArray = champsError[key];
            champArray.forEach((champ: Champion) => {
              setChampionDictionary((prevDictionary) => {
                const updatedDictionary = { ...prevDictionary };
                if (!updatedDictionary[key]) {
                  updatedDictionary[key] = [champ];
                } else {
                  if (!updatedDictionary[key].some(existingChamp => existingChamp.id === champ.id)) {
                    updatedDictionary[key] = [champ, ...updatedDictionary[key]];
                  }
                }
                return updatedDictionary;
              });
            });
          }
        }
      }

      const championCorrectDictionaryStorage = localStorage.getItem('championCorrectDictionaryStorage');
      if (championCorrectDictionaryStorage) {
        const champsCorrect = JSON.parse(championCorrectDictionaryStorage);
        Object.keys(champsCorrect).forEach((key) => {
          addImg(key, champsCorrect[key], 0);
        });
      }
    }
  }, []);


  // Clear input and suggestions when square changes
  useEffect(() => {
    setInputValue("")
    setChampsSuggested([])
  }, [squareId])

  // Save championDictionary in local storage
  useEffect(() => {
    if (Object.values(championDictionary).some((champions) => champions.length > 0)) {
      localStorage.setItem('championDictionaryStorage', JSON.stringify(championDictionary));    
    }
  }, [championDictionary]);

  // Save championsCorrectDictionary in local storage
  useEffect(() => {
    if (Object.keys(championsCorrectDictionary).length > 0) {
      localStorage.setItem('championCorrectDictionaryStorage', JSON.stringify(championsCorrectDictionary));
    }
  }, [championsCorrectDictionary]);
  
  // Handle win conditions
  useEffect(() => {
    if(winGame===9) {
      handleWin()
      setGameOver(true);
      const winFinished = localStorage.getItem("winFinished")
      let winStreakStorage: number = parseInt(localStorage.getItem("winStreakStorage") ?? "0", 10);
      if(winStreakStorage >= 0 && winFinished === "false") {
        setWinStreak(winStreak + 1)
        localStorage.setItem("winFinished", "true")
        winStreakStorage = winStreakStorage + 1;
        localStorage.setItem("winStreakStorage", winStreakStorage + "")
      }
    };
  }, [winGame]);

  useEffect(() => {
    if (lifes === 0) {
      handleDefeat();
      setWinStreak(0)
      localStorage.setItem('gameOverStorage', "true");
      localStorage.setItem("winStreakStorage", "0");
      setGameOver(true);
    }
  }, [lifes]);

  return (
    <div className="Board">
      
      <div id='board-game' className='board-game'>
        
        <LoseModal 
          isOpen={isDefeatModalOpen} 
          onClose={handleCloseDefeatModal}
        />

        <WinModal 
          isOpen={isWinModalOpen} 
          onClose={handleCloseWinModal}
          lifes={lifes}
          winStreak={winStreak}
        />

        <div className='mainContent'>
          <Lifes 
            lifes={lifes}
            maxLifes={maxLifes}
          />
          <Square 
            board={board} 
            squareSelected={squareSelected}
            gameOver={gameOver}
            isCorrectSquare={isCorrectSquare}
            squaresCorrects={squaresCorrects}
            clues={clues}
          />

        </div>

      </div>

      <div className='containerChamps'>

        <InputSuggestion 
          winGame={winGame} 
          gameOver={gameOver} 
          inputValue={inputValue} 
          setInputValue={setInputValue} 
          setChampsSuggested={setChampsSuggested} 
          champs={champs} 
          championDictionary={championDictionary} 
          squareId={squareId} 
          squareSelected={squareSelected}
          isCorrectSquare={isCorrectSquare}
        />
        
        <IncorrectChamp 
          squareId={squareId} 
          championDictionary={championDictionary}
        />

        <Suggested 
          champsSuggested={champsSuggested} 
          squareSelected={squareSelected} 
          inputValue={inputValue} 
          squareId={squareId} 
          isChampionValid={isChampionValid} 
          uploadBoard={uploadBoard} 
          setInputValue={setInputValue} 
          setChampsSuggested={setChampsSuggested}
        />

      </div>

    </div>
  );
}
